// src/FbmCard.jsx — Order Detail card for Amazon FBM orders + the UPS/USPS rate picker (Eric, 2026-10-13).
import React, { useState } from 'react';
import { Card, BlockStack, InlineStack, Text, Badge, Link, Modal, RadioButton, Tooltip, Banner } from '@shopify/polaris';

const STEP_LABELS = { moved: 'Moved', fulfilled: 'Fulfilled', label: 'Label', first_scan: 'First scan', vr_created: 'VR created', vr_lines: 'Lines verified', vr_note: 'Note', vr_sent: 'Sent', writeback: 'Written back' };
const DOT = { done: '#2e7d32', pending: '#c9cccf', failed: '#d72c0d', paused: '#b98900', waiting_har: '#b98900', skipped: 'transparent' };
const WAITING_TEXT = { vr_sent: 'waiting for the mark-sent HAR' };
const fmtDT = iso => iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

export function FbmCard({ fbm }) {
    if (!fbm) return null;
    const problems = Object.keys(STEP_LABELS).filter(s => ['failed', 'paused', 'waiting_har'].includes(fbm.steps?.[s]?.status));
    return (
        <Card>
            <BlockStack gap="200">
                <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingMd" as="h3">Amazon FBM</Text>
                    <Badge tone={fbm.stage === 'failed' ? 'critical' : fbm.stage === 'done' ? 'success' : 'info'}>{String(fbm.stage || '').replace(/_/g, ' ')}</Badge>
                </InlineStack>
                <InlineStack gap="100">
                    {Object.keys(STEP_LABELS).map(s => { const st = fbm.steps?.[s] || {}; const tip = `${STEP_LABELS[s]}: ${st.status || 'pending'} ${fmtDT(st.at)}${st.error ? ' — ' + st.error : ''}`;
                        return <Tooltip key={s} content={tip}><span aria-label={tip} style={{ width: 12, height: 12, borderRadius: 6, display: 'inline-block', background: DOT[st.status] || DOT.pending, border: st.status === 'skipped' ? '1px solid #c9cccf' : 'none' }} /></Tooltip>; })}
                </InlineStack>
                {fbm.inv_correct_error && <Banner tone="critical" title="Store inventory correction failed"><p>{fbm.inv_correct_error}</p></Banner>}
                {problems.length > 0 && (
                    <BlockStack gap="050">
                        {problems.map(s => { const st = fbm.steps[s];
                            return <Text key={s} as="p" variant="bodySm" tone={st.status === 'failed' ? 'critical' : 'caution'}>{STEP_LABELS[s]}: {st.status} — {st.error || (WAITING_TEXT[s] || 'waiting for the Lightspeed admin session')}</Text>; })}
                    </BlockStack>
                )}
                <Text as="p" variant="bodySm">Recommended: {fbm.recommended_name || '—'}{fbm.backup_name ? ` · Backup: ${fbm.backup_name}` : ''}</Text>
                {fbm.moved_to_name && <Text as="p" variant="bodySm">Moved to {fbm.moved_to_name} by {fbm.moved_by} {fmtDT(fbm.moved_at)}</Text>}
                {fbm.tracking?.number && <Text as="p" variant="bodySm">{fbm.tracking.carrier} {fbm.tracking.url ? <Link url={fbm.tracking.url} external>{fbm.tracking.number}</Link> : fbm.tracking.number} · {fbm.tracking.status || 'awaiting scan'} {fmtDT(fbm.tracking.status_at)}</Text>}
                {fbm.vr_url && <Text as="p" variant="bodySm">Lightspeed vendor return <Link url={fbm.vr_url} external>#{fbm.vr_id}</Link></Text>}
            </BlockStack>
        </Card>
    );
}

export function FbmRatePicker({ open, rates, onPick, onClose, amazonTier }) {
    const [sel, setSel] = useState(() => Math.max(0, (rates || []).findIndex(r => r.carrier_code === 'ups_shipping' && r.service_code === '03')));
    return (
        <Modal open={open} title="Choose a shipping service" onClose={onClose}
               primaryAction={{ content: 'Buy label & print', disabled: !rates?.length, onAction: () => onPick(rates[sel]) }}
               secondaryActions={[{ content: 'Cancel', onAction: onClose }]}>
            <Modal.Section>
                <BlockStack gap="100">
                    {amazonTier && (
                        <Banner tone={amazonTier.fallback ? 'warning' : 'info'}>
                            {amazonTier.fallback
                                ? `${amazonTier.method}: no ${amazonTier.label.replace(' only', '')} came back for this address, so every service is listed — pick the closest match.`
                                : `${amazonTier.method}: ${amazonTier.label}${amazonTier.hidden ? ` (${amazonTier.hidden} other service${amazonTier.hidden === 1 ? '' : 's'} hidden)` : ''}.`}
                        </Banner>
                    )}
                    {(rates || []).map((r, i) => (
                        <RadioButton key={`${r.carrier_code}:${r.service_code}`} label={`${r.title} — $${Number(r.price).toFixed(2)}`} checked={sel === i} onChange={() => setSel(i)} name="fbm-rate" />
                    ))}
                    {!rates?.length && <Text as="p" tone="critical">No UPS or USPS rates came back for this address.</Text>}
                </BlockStack>
            </Modal.Section>
        </Modal>
    );
}
